import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { ScrollText, Sparkles, Loader2, ExternalLink, CheckCircle2, ShoppingBag } from "lucide-react";

const PAGE_TYPES = ["terms", "privacy", "dropshipping_disclosure", "refund", "shipping", "imprint"];

export function LegalPanel() {
  const { lang } = useLang();
  const [pages, setPages] = useState({});
  const [active, setActive] = useState("dropshipping_disclosure");
  const [busy, setBusy] = useState(null); // "generate"|"approve"|"publish"|null
  const [editorBody, setEditorBody] = useState("");
  const [editorTitle, setEditorTitle] = useState("");

  const load = async () => {
    const { data } = await api.get("/legal/pages");
    const map = Object.fromEntries((data.pages || []).map((p) => [p.page_type, p]));
    setPages(map);
  };

  useEffect(() => { load(); }, []);

  const current = pages[active];
  useEffect(() => {
    setEditorBody((current?.body_md?.[lang]) || current?.body_md?.en || "");
    setEditorTitle((current?.title?.[lang]) || current?.title?.en || "");
  }, [active, current, lang]);

  const generate = async () => {
    setBusy("generate");
    try {
      await api.post(`/legal/generate/${active}`, {});
      toast.success(t(lang, "legal_generated"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const approve = async () => {
    if (!current) return;
    setBusy("approve");
    try {
      const newBody = { ...(current.body_md || {}), [lang]: editorBody };
      const newTitle = { ...(current.title || {}), [lang]: editorTitle };
      await api.post(`/legal/pages/${active}/approve`, { body_md: newBody, title: newTitle });
      toast.success(t(lang, "legal_approved"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const publishShopify = async () => {
    setBusy("publish");
    try {
      const { data } = await api.post(`/legal/pages/${active}/publish-shopify`);
      toast.success(t(lang, "legal_published_shopify"));
      window.open(data.url, "_blank", "noreferrer");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const generateAll = async () => {
    if (!window.confirm("Gerar TODAS as 6 páginas legais em 5 idiomas via GPT-5.2? Demora ~2min.")) return;
    setBusy("generate-all");
    try {
      const { data } = await api.post("/legal/generate-all");
      toast.success(`Geradas ${data.generated}/${data.total} páginas legais`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const approveAll = async () => {
    setBusy("approve-all");
    try {
      const { data } = await api.post("/legal/approve-all");
      toast.success(`${data.approved} páginas aprovadas`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const publishAllShopify = async () => {
    if (!window.confirm("Publicar TODAS as páginas aprovadas no Shopify?")) return;
    setBusy("publish-all");
    try {
      const { data } = await api.post("/legal/publish-all-shopify");
      toast.success(`${data.published}/${data.total} publicadas no Shopify`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="hq-card overflow-hidden" data-testid="legal-panel">
      <div className="p-5 border-b border-white/10 flex items-center gap-2 flex-wrap">
        <ScrollText size={14} className="text-[color:var(--hq-cyan)]" />
        <div className="font-display text-lg">{t(lang, "legal_title")}</div>
        <span className="ml-auto font-mono text-[10px] uppercase text-white/40">GPT-5.2 · 5 languages</span>
      </div>

      {/* Bulk actions — one-click compliance for a Swiss/EU dropshipping store */}
      <div className="p-4 border-b border-white/10 bg-[color:var(--hq-cyan)]/[0.03] flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase text-white/50 mr-1">Compliance rápido:</span>
        <button
          data-testid="legal-generate-all-btn"
          onClick={generateAll}
          disabled={busy !== null}
          className="hq-btn hq-btn-primary text-xs disabled:opacity-50"
        >
          {busy === "generate-all" ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
          Gerar todas (6 páginas · 5 idiomas)
        </button>
        <button
          data-testid="legal-approve-all-btn"
          onClick={approveAll}
          disabled={busy !== null}
          className="hq-btn hq-btn-cyan text-xs disabled:opacity-50"
        >
          {busy === "approve-all" ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
          Aprovar todas
        </button>
        <button
          data-testid="legal-publish-all-shopify-btn"
          onClick={publishAllShopify}
          disabled={busy !== null}
          className="hq-btn hq-btn-ghost text-xs disabled:opacity-50"
        >
          {busy === "publish-all" ? <Loader2 size={10} className="animate-spin" /> : <ShoppingBag size={10} />}
          Publicar todas no Shopify
        </button>
      </div>

      <div className="p-5 border-b border-white/10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="legal-tabs">
        {PAGE_TYPES.map((pt) => {
          const p = pages[pt];
          const status = p?.published
            ? { key: "published", label: t(lang, "legal_status_published"),
                cls: "text-[color:var(--hq-success)] border-[color:var(--hq-success)]/40 bg-[color:var(--hq-success)]/5" }
            : p?.approved
            ? { key: "approved", label: t(lang, "legal_status_approved"),
                cls: "text-[color:var(--hq-cyan)] border-[color:var(--hq-cyan)]/40 bg-[color:var(--hq-cyan)]/5" }
            : p
            ? { key: "draft", label: t(lang, "legal_status_draft"),
                cls: "text-[color:var(--hq-warning)] border-[color:var(--hq-warning)]/40 bg-[color:var(--hq-warning)]/5" }
            : { key: "empty", label: t(lang, "legal_status_empty"),
                cls: "text-white/40 border-white/10 bg-white/[0.02]" };
          const isActive = active === pt;
          return (
            <button
              key={pt}
              data-testid={`legal-tab-${pt}`}
              onClick={() => setActive(pt)}
              className={`text-left border rounded-lg p-4 transition-all duration-200 ${
                isActive
                  ? "border-[color:var(--hq-cyan)] bg-[color:var(--hq-cyan)]/[0.04]"
                  : "border-white/10 bg-white/[0.02] hover:border-white/20"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-white font-medium">
                  {t(lang, `legal_pt_${pt}`)}
                </div>
                {p?.published && <CheckCircle2 size={12} className="text-[color:var(--hq-success)]" />}
              </div>
              <div className={`inline-flex text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 rounded-full ${status.cls}`}>
                {status.label}
              </div>
            </button>
          );
        })}
      </div>

      <div className="p-5 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="legal-generate-btn"
            onClick={generate}
            disabled={busy !== null}
            className="hq-btn hq-btn-primary text-xs disabled:opacity-50"
          >
            {busy === "generate" ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
            {current ? t(lang, "legal_regenerate") : t(lang, "legal_generate")}
          </button>
          {current && (
            <>
              <button
                data-testid="legal-approve-btn"
                onClick={approve}
                disabled={busy !== null}
                className="hq-btn hq-btn-cyan text-xs disabled:opacity-50"
              >
                {busy === "approve" ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
                {t(lang, "legal_approve")}
              </button>
              {current.published && (
                <button
                  data-testid="legal-publish-shopify-btn"
                  onClick={publishShopify}
                  disabled={busy !== null}
                  className="hq-btn hq-btn-ghost text-xs disabled:opacity-50"
                >
                  {busy === "publish" ? <Loader2 size={10} className="animate-spin" /> : <ShoppingBag size={10} />}
                  {t(lang, "legal_publish_shopify")}
                </button>
              )}
              {current.shopify_url && (
                <a
                  href={current.shopify_url}
                  target="_blank"
                  rel="noreferrer"
                  className="hq-btn hq-btn-ghost text-xs"
                  data-testid="legal-shopify-link"
                >
                  {t(lang, "legal_view_shopify")} <ExternalLink size={10} />
                </a>
              )}
            </>
          )}
        </div>

        {current && (
          <>
            <div>
              <div className="font-mono uppercase text-[10px] text-white/40 mb-1">{t(lang, "legal_edit_title")} ({lang})</div>
              <input
                data-testid="legal-title-input"
                value={editorTitle}
                onChange={(e) => setEditorTitle(e.target.value)}
                className="hq-input w-full"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono uppercase text-[10px] text-white/40">{t(lang, "legal_edit_body")} ({lang})</span>
                {current.published && (
                  <span className="font-mono text-[10px] uppercase text-[color:var(--hq-success)]">
                    · {t(lang, "legal_status_published")}
                  </span>
                )}
              </div>
              <textarea
                data-testid="legal-body-input"
                value={editorBody}
                onChange={(e) => setEditorBody(e.target.value)}
                className="hq-input w-full h-96 font-mono text-xs"
              />
            </div>
            <div className="font-mono text-[10px] text-white/40">
              {t(lang, "legal_last_updated")}: {current.last_updated_iso}
            </div>
          </>
        )}
        {!current && (
          <div className="py-8 text-center font-mono text-xs text-white/40">
            {t(lang, "legal_none")}
          </div>
        )}
      </div>
    </div>
  );
}
